import "./styles.css";
import { FC, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import useMutation from "../../../../../shared/components/hooks/useMutation";
import EditIcon from "@mui/icons-material/Edit";
import InputTextfield from "../../../../../shared/components/textField";
import { FormContainer } from "../../../../../shared/components/formContainer";
import { getTextFields } from "../variable/textField";
import { customizeProfileValidationSchema } from "../variable/customSchema";
import { Gender } from "../../../../../shared/enum";
import { ZSSelectBox } from "../../../../../shared/components/selectBox";
import ProfileVerticalContainer from "../../../../../shared/components/profileVerticalContainer";
// import ProfileVerticalContainer from "../../../../../shared/components/profileVerticalContainer";

type ProfileEditViewProps = {
  fetchData: Function;
  profileData: {
    id: string;
    email: string;
    phone: number;
    admin: {
      id: string;
      name: string;
      gender: string;
      designation: string;
      secondaryEmail: string;
    };
  };
  entityPermission?: Object[];
};
export const ProfileEditView: FC<ProfileEditViewProps> = ({
  fetchData,
  profileData,
  entityPermission,
}) => {
  const { loading: apiLoading, modifyData } = useMutation();
  const [editStatus, setEditStatus] = useState(false);
  const profileEditFields = getTextFields(profileData);
  const initialValues = {
    email: profileData?.email ?? "",
    phone: profileData?.phone ?? "",
    name: profileData?.admin?.name ?? "",
    gender: profileData?.admin?.gender ?? "",
    designation: profileData?.admin?.designation ?? "",
    secondaryEmail: profileData?.admin?.secondaryEmail ?? "",
  };

  const profileForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: customizeProfileValidationSchema(editStatus),
    onSubmit: async (formData: any) => {
      let response;
      response = await modifyData(
        `/admin/user/profile-update`,
        "put",
        {
          ...formData,
        },
        profileForm
      );

      if (response) {
        fetchData();
        closeAdminEdit();
      }
    },
  });

  const closeAdminEdit = () => {
    setEditStatus(false);
  };

  const editPermission = entityPermission
    ? entityPermission?.includes("User Profile-Update")
    : true;

  const profileEditTooltipNode = editPermission && (
    <Tooltip arrow title="Edit">
      <IconButton
        className="TitleBarIcon"
        size="medium"
        id={`Profile-edit`}
        onClick={() => setEditStatus(true)}
        aria-label={`profile-edit`}
        disabled={
          entityPermission
            ? !entityPermission?.includes("User Profile-Update")
            : false
        }
      >
        <EditIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <ProfileVerticalContainer
        entityFields={profileEditFields}
        entityTitle="Profile Details"
        tooltips={profileEditTooltipNode}
      />

      <Dialog
        open={editStatus}
        onClose={closeAdminEdit}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { borderRadius: 16, padding: "32px" } }}
      >
        <DialogTitle>Edit Profile</DialogTitle>
        <Divider />
        <DialogContent>
          <FormContainer>
            <Box className="form-border-custom">
              <Grid
                container
                alignItems="stretch"
                columnSpacing={4}
                rowSpacing={2}
              >
                {profileEditFields.map(
                  (field: any, index: number) =>
                    (field?.formField === undefined ||
                      field.formField === true) && (
                      <Grid item md={6} sm={6} xs={12} key={field.id}>
                        <InputTextfield
                          form={profileForm}
                          fieldProps={field}
                          key={index}
                          disabled={field?.disabled}
                        />
                      </Grid>
                    )
                )}
                <Grid item md={6} sm={6} xs={12} key="gender">
                  <ZSSelectBox
                    fieldProps={{
                      id: "member-gender",
                      name: "gender",
                      label: "Gender",
                      placeholder: "Select an option",
                      required: true,
                    }}
                    options={[
                      { label: Gender.Male, value: Gender.Male },
                      { label: Gender.Female, value: Gender.Female },
                      { label: Gender.Other, value: Gender.Other },
                    ]}
                    form={profileForm}
                    value={profileForm.values?.gender}
                  />
                </Grid>
              </Grid>
            </Box>
          </FormContainer>
        </DialogContent>
        <DialogActions sx={{ px: 5 }}>
          <Button onClick={closeAdminEdit} color="warning" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            onClick={() => profileForm.handleSubmit()}
            color="primary"
            variant="contained"
            disabled={apiLoading}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileEditView;
